import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const success = await login(credentials.username, credentials.password);

    if (success) {
      toast.success('Inicio de sesión exitoso');
      navigate('/admin');
    } else {
      toast.error('Credenciales incorrectas', {
        description: 'Usuario o contraseña inválidos',
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[url('https://images.unsplash.com/photo-1647118868186-70d38e10b0dc?q=80&w=2400')] bg-cover bg-center bg-fixed relative flex items-center justify-center p-4">
      {/* Capa oscura (Overlay) para legibilidad */}
      <div className="absolute inset-0 bg-black/85 z-0"></div>
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <div className="bg-black/50 p-3 rounded-full border border-gray-800 shadow-[0_0_20px_rgba(57,255,20,0.2)] mb-4 inline-block">
            <img 
              src="/fu-logo.png" 
              alt="Inversiones FU Logo" 
              className="h-20 w-20 object-contain drop-shadow-[0_0_10px_rgba(57,255,20,0.5)]" 
            />
          </div>
          <h1 className="text-3xl font-black italic tracking-widest text-white uppercase drop-shadow-lg">
            INVERSIONES <span className="text-[#39FF14]">FU</span>
          </h1>
          <p className="text-[#39FF14] text-xs font-bold tracking-widest uppercase mt-2">Sistema Central</p>
        </div>

        {/* Login Card */}
        <Card className="bg-card border-border shadow-elevated">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Panel Administrativo</CardTitle>
            <CardDescription>Ingresa tus credenciales para acceder</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="admin"
                    value={credentials.username}
                    onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                    className="pl-10 bg-background border-border"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="pl-10 bg-background border-border"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold glow-neon"
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-muted-foreground hover:text-foreground"
              >
                Volver al catálogo
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
